import { API_ENDPOINTS } from "../config/api";
import authHttpClient from "../scenes/utils/authHttpClient";
import axiosInstance from "../scenes/utils/axiosInstance";
import { resetSessionActivity } from "../utils/sessionActivity";
import {
  establishDoctorSession,
  clearDoctorSessionStorage,
  isTenantSessionActive,
  isDoctorSessionActive,
} from "../utils/authSession";

/**
 * 🔐 Authentication Service
 * Handles doctor invitation completion and authentication flows
 */
class AuthService {
  /**
   * 🆕 Davet linkinden kayıt tamamlama
   * @param {Object} data - Registration data
   * @param {string} data.token - Invitation token from email link
   * @param {string} data.name - Doctor's first name
   * @param {string} data.surname - Doctor's last name
   * @param {string} data.password - Password (min 6 characters)
   * @param {string} data.specialization - Medical specialization
   * @param {string} data.phone_number - Phone number
   * @returns {Promise<Object>} Response with doctor info and JWT token
   */
  completeDoctorInvitation = async (data) => {
    try {
      const response = await authHttpClient.post(API_ENDPOINTS.AUTH.COMPLETE_INVITATION, data);
      return response.data;
    } catch (error) {
      console.error("Davet tamamlama sırasında hata oluştu:", error);
      throw error;
    }
  };

  /**
   * 🔍 Davet token doğrulama (optional - token decode için)
   * @param {string} token - Invitation token
   * @returns {Promise<Object>} Token payload (email, role, tenant_id)
   */
  verifyInvitationToken = async (token) => {
    try {
      // Backend'de token verify edilecek, burada sadece decode edebiliriz
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Token decode hatası:", error);
      throw new Error("Geçersiz davet linki");
    }
  };

  /**
   * 💾 Login sonrası token ve user bilgilerini kaydet
   * @param {string} token - JWT token
   * @param {Object} doctor - Doctor object
   */
  saveAuthData = (_token, doctor, tenant) => {
    resetSessionActivity();
    establishDoctorSession({
      id: doctor.id,
      email: doctor.email,
      role: doctor.role,
      tenant_id: doctor.tenant_id,
      name: doctor.name,
      surname: doctor.surname,
      specialization: doctor.specialization,
      accepted_terms_of_service: doctor.accepted_terms_of_service,
      accepted_terms_of_service_at: doctor.accepted_terms_of_service_at,
      terms_of_service_version: doctor.terms_of_service_version,
      accepted_limited_data_processing: doctor.accepted_limited_data_processing,
      accepted_limited_data_processing_at: doctor.accepted_limited_data_processing_at,
      limited_data_processing_version: doctor.limited_data_processing_version,
      tenant_type: tenant?.tenant_type || doctor.tenant_type,
    });
  };

  /**
   * ✍️ Güncellenen Kullanıcı Sözleşmesi'ni Onayla
   * @param {string} [version="4 Eylül 2026"] - Sözleşme metin sürümü
   */
  acceptTermsOfService = async (version = "4 Eylül 2026") => {
    const isTenant = isTenantSessionActive() && !isDoctorSessionActive();
    const client = isTenant
      ? (await import("../scenes/utils/tenantAxiosInstance")).default
      : (await import("../scenes/utils/axiosInstance")).default;
    const response = await client.post(API_ENDPOINTS.AUTH.ACCEPT_TERMS, { version });
    return response.data;
  };

  /**
   * ✍️ Sınırlı Veri İşleme Taahhütnamesi'ni Onayla (Bireysel Hekimler için)
   * @param {string} [version="11 Eylül 2026"] - Taahhütname metin sürümü
   */
  acceptLimitedDataProcessing = async (version = "11 Eylül 2026") => {
    const axiosInstance = (await import("../scenes/utils/axiosInstance")).default;
    const response = await axiosInstance.post(API_ENDPOINTS.AUTH.ACCEPT_LIMITED_DATA_PROCESSING, { version });
    return response.data;
  };

  /**
   * 🚪 Logout
   */
  logout = () => {
    clearDoctorSessionStorage();
    localStorage.removeItem("languageManuallySet");
  };

  /**
   * Kiosk çıkış kilidi. 422 → false (logout yok). Token üretmez.
   */
  verifyPassword = async (password) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.AUTH.VERIFY_PASSWORD, { password });
      return response.data?.ok === true;
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 400) {
        return false;
      }
      throw error;
    }
  };

  /**
   * Kiosk PIN durumunu sorgula
   * @returns {Promise<{ hasPin: boolean }>}
   */
  getKioskPinStatus = async () => {
    const response = await axiosInstance.get(API_ENDPOINTS.AUTH.KIOSK_PIN_STATUS);
    return response.data;
  };

  /**
   * İlk kez Kiosk PIN oluştur
   * @param {Object} payload
   * @param {string} payload.pin
   * @param {string} payload.confirmPin
   * @returns {Promise<{ ok: boolean, hasPin: boolean }>}
   */
  setKioskPin = async ({ pin, confirmPin }) => {
    const response = await axiosInstance.post(API_ENDPOINTS.AUTH.KIOSK_PIN, { pin, confirmPin });
    return response.data;
  };

  /**
   * Mevcut Kiosk PIN'i güncelle
   * @param {Object} payload
   * @param {string} payload.currentPin
   * @param {string} payload.pin
   * @param {string} payload.confirmPin
   * @returns {Promise<{ ok: boolean, hasPin: boolean }>}
   */
  updateKioskPin = async ({ currentPin, pin, confirmPin }) => {
    const response = await axiosInstance.put(API_ENDPOINTS.AUTH.KIOSK_PIN, { currentPin, pin, confirmPin });
    return response.data;
  };

  /**
   * Hesap şifresi ile Kiosk PIN sıfırla / yeniden belirle
   * @param {Object} payload
   * @param {string} payload.password - Hesap giriş şifresi
   * @param {string} payload.pin
   * @param {string} payload.confirmPin
   * @returns {Promise<{ ok: boolean, hasPin: boolean }>}
   */
  resetKioskPin = async ({ password, pin, confirmPin }) => {
    const response = await axiosInstance.post(API_ENDPOINTS.AUTH.KIOSK_PIN_RESET, { password, pin, confirmPin });
    return response.data;
  };

  /**
   * Kiosk çıkışı için PIN doğrula.
   * 422/400 → false (logout yok).
   * 429 → throw (FAZ 5 oran aşımını ayrı mesajla yakalayabilsin).
   * @param {string|{pin: string}} pinOrPayload
   * @returns {Promise<boolean>}
   */
  verifyKioskPin = async (pinOrPayload) => {
    const pin =
      typeof pinOrPayload === "object" && pinOrPayload !== null
        ? pinOrPayload.pin
        : pinOrPayload;
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.AUTH.VERIFY_KIOSK_PIN, { pin });
      return response.data?.ok === true;
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 400) {
        return false;
      }
      throw error;
    }
  };
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new AuthService();
